import React from "react";

import { toggleSavePlan } from "ComparePlans/redux";
import { useGetCurrentCompanyApplicationQuery } from "EmployerApplication/api";
import { DesktopCloseButton } from "EmployerApplication/Components/FormSections/DesktopCloseButton";
import { RemovePlanButton } from "EmployerApplication/Components/FormSections/ShoppingCartPlanCardV2";
import isIntuit from "Intuit/isIntuit";
import { PlanDetailsDumb } from "QuotePage/app/PlanDetails";
import PlanDetailsHeader from "QuotePage/app/planDetails/Header";
import { isRenewal, renewalBaseName } from "QuotePage/urls";
import { connect } from "react-redux";
import { withRouter, Link } from "react-router-dom";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import { companyTokenString } from "Utils/companyToken";
import formatCurrency from "Utils/formatCurrency";
import { routerUrlHelper } from "Utils/routerUrl";
import { planName, planDeductible } from "Utils/simplifyPlanName";

import Header from "Cobalt/Header";
import { ReduxTriggerButton, ReduxTrigger, FullScreenModal, Modal, ModalBody, ModalHeader } from "Cobalt/Modal";
import BodyText from "Common/BodyText";
import Button from "Common/Button";
import { Card, CardSection, CardHeader } from "Common/Card";
import { CarrierLogoFlexbox } from "Common/CarrierLogo";
import { getSelectedMedicalPlans, getSelectedDentalPlan, getSelectedVisionPlan } from "Common/CartService";
import { Row, Col } from "Common/Grid";
import { Ol, Li } from "Common/HTML";
import PlanDetailsModal from "Common/plans/PlanDetailsModal";
import { appendAuthParams } from "Common/TokenAuth/Utils";

import { FAQCard } from "./compactQuotePage/ExpandingFAQs";

import makeBem from "Utils/makeBem";

const bem = makeBem("QuotePageCartModal");

const MoreDetailsButton = ({ plan }) => (
  <ReduxTriggerButton name={plan.full_display_name} pl={0} pb={2} pt={2}>
    View Plan Details
  </ReduxTriggerButton>
);

const MedicalPlanDetailsModal = ({ plan }) => (
  <ReduxTrigger
    name={plan.full_display_name}
    modal={(props) => (
      <FullScreenModal className="more-details-modal" {...props}>
        <ModalHeader key="header" closeButton={isIntuit()}>
          {!isIntuit() && <DesktopCloseButton onHide={props.onHide} />}
          <Header level={7} mb={0}>
            <PlanDetailsHeader plan={plan} isSampleQuote={false} inCart />
          </Header>
        </ModalHeader>
        <ModalBody key="body" className="py-0 more-details-modal-body">
          <PlanDetailsDumb plan={plan} inCart isAlternateCart />
        </ModalBody>
      </FullScreenModal>
    )}
  />
);

const PlanCost = ({ plan }) => (
  <div>
    <BodyText size="medium" weight="normal" componentClass="span">
      {formatCurrency(plan.average_monthly_premium)}
    </BodyText>
    <BodyText size="medium" weight="normal" componentClass="span">
      /mo
    </BodyText>
    <br />
    <BodyText size="medium" weight="normal" componentClass="span">
      per employee
    </BodyText>
  </div>
);

const PlanName = ({ plan }) => (
  <BodyText>
    {planName(plan)} {planDeductible(plan)}
  </BodyText>
);

const answerComparePlans = (onToggleSavedPlan, selectedMedicalPlans, cartOnHide) => (
  <>
    <BodyText pb={4}>You can use the compare feature to see a side-by-side view of the plans’ prices.</BodyText>
    <Ol>
      <Li pb={4}>Select the checkbox for the plans that you want to compare</Li>
      <Li pb={4}>Click on the “Compare” button that appears at the bottom of the page</Li>
      <img
        alt="A table showing a list of sample insurance plans.
        Each insurance plan has a checkbox positioned left of the carrier's logo.
        The checkbox for the plans to be compared have been selected and circled.
        At the bottom of the screen, a button titled 'Compare' has been circled. The list plans to be compared
        are positioned right of this button"
        src="/images/faq-images/compare_plans.png"
      />
    </Ol>
    <ReduxTriggerButton
      name="Compare Plans"
      onClick={() => {
        selectedMedicalPlans.forEach((plan) => onToggleSavedPlan(plan.id, "plan_card_bottom_right", true));

        cartOnHide();
      }}
      pl={0}
      pb={2}
      pt={0}
    >
      Compare your selected plan to others
    </ReduxTriggerButton>
  </>
);

const answerReplacePlans = (
  <BodyText pb={4}>
    If you select to change your plan, employees who selected to enroll in the old plan will need to re-do their selections, and we may require additional
    information during the application.
  </BodyText>
);

const ContinueApplicationButton = ({ ctaUrl }) => {
  const url = appendAuthParams(routerUrlHelper(`${companyTokenString()}${ctaUrl}`));

  return isRenewal() ? (
    <Button href={url} fullWidth>
      Continue Application
    </Button>
  ) : (
    <Button component={Link} to={ctaUrl} fullWidth>
      Continue Application
    </Button>
  );
};

export const QuoteInformationPanel = ({ selectedMedicalPlans, selectedDentalPlan, selectedVisionPlan, ctaUrl }) => (
  <>
    <Card theme="flat" p={0}>
      {selectedMedicalPlans.map((plan, i) => (
        <CardSection p={4} key={i}>
          <BodyText weight="bold" pb={2}>
            Medical Plan
          </BodyText>
          <>
            <Row>
              <Col md={6}>
                <CarrierLogoFlexbox plan={plan} maxSize={3} pb={2} />
                <PlanName plan={plan} />
              </Col>
              <Col md={4}>
                <PlanCost plan={plan} />
              </Col>
              <Col md={2}>
                <RemovePlanButton plan={plan} lastPlan={selectedMedicalPlans.length == 1} />
              </Col>
            </Row>
            <MoreDetailsButton plan={plan} />
            <br />
            <MedicalPlanDetailsModal plan={plan} />
          </>
        </CardSection>
      ))}

      {selectedDentalPlan && (
        <CardSection>
          <BodyText weight="bold" pb={2}>
            Dental Plan
          </BodyText>
          <Row>
            <Col md={6}>
              <CarrierLogoFlexbox plan={selectedDentalPlan} maxSize={3} pb={2} />

              <PlanName plan={selectedDentalPlan} />
            </Col>
            <Col md={4}>
              <PlanCost plan={selectedDentalPlan} />
            </Col>
            <Col md={2}>
              <RemovePlanButton plan={selectedDentalPlan} lastPlan={false} />
            </Col>
          </Row>
          <MoreDetailsButton plan={selectedDentalPlan} />
          <br />
          <PlanDetailsModal plan={selectedDentalPlan} inCart />
        </CardSection>
      )}

      {selectedVisionPlan && (
        <CardSection>
          <BodyText weight="bold" pb={2}>
            Vision Plan
          </BodyText>
          <Row>
            <Col md={6}>
              <CarrierLogoFlexbox plan={selectedVisionPlan} maxSize={3} pb={2} />

              <PlanName plan={selectedVisionPlan} />
            </Col>
            <Col md={4}>
              <PlanCost plan={selectedVisionPlan} />
            </Col>
            <Col md={2}>
              <RemovePlanButton plan={selectedVisionPlan} lastPlan={false} />
            </Col>
          </Row>
          <MoreDetailsButton plan={selectedVisionPlan} />
          <br />
          <PlanDetailsModal plan={selectedVisionPlan} inCart />
        </CardSection>
      )}
    </Card>
    {selectedMedicalPlans.length > 0 && <ContinueApplicationButton ctaUrl={ctaUrl} />}
  </>
);

const FAQPanel = ({ answerReplacePlans, answerComparePlans, onToggleSavedPlan, selectedMedicalPlans, cartOnHide }) => (
  <Row>
    <BodyText px={4} pt={4} weight="bold">
      How Can We Help?
    </BodyText>
    <CardSection px={4}>
      <FAQCard question="How do I compare one plan to another?" answer={answerComparePlans(onToggleSavedPlan, selectedMedicalPlans, cartOnHide)} />

      <FAQCard question="What happens if I replace this plan with a new plan?" answer={answerReplacePlans} />
    </CardSection>
  </Row>
);

const QuotePageCartModalDumb = ({ history, selectedMedicalPlans, selectedDentalPlan, selectedVisionPlan, onToggleSavedPlan }) => {
  const { data: companyApplication } = useGetCurrentCompanyApplicationQuery();
  const cartOnHide = () => {
    const { pathname } = history.location;
    const cutoffPoint = pathname.indexOf("/cart");
    history.push(`${pathname.substring(0, cutoffPoint)}`);
  };
  return (
    <FullScreenModal show onHide={cartOnHide} className={bem("dialog")}>
      <ModalHeader key="header" closeButton>
        <CardHeader p={0}>Your Cart</CardHeader>
      </ModalHeader>
      <ModalBody key="body" className="more-details-modal-body" style={{ overflowY: "auto" }}>
        {selectedMedicalPlans.length > 0 || selectedDentalPlan || selectedVisionPlan ? (
          <>
            <QuoteInformationPanel
              selectedMedicalPlans={selectedMedicalPlans}
              selectedDentalPlan={selectedDentalPlan}
              selectedVisionPlan={selectedVisionPlan}
              ctaUrl={isRenewal() ? renewalBaseName : companyApplication?.applicationStatus.nextSectionUrl}
            />

            <FAQPanel
              answerReplacePlans={answerReplacePlans}
              answerComparePlans={answerComparePlans}
              onToggleSavedPlan={onToggleSavedPlan}
              selectedMedicalPlans={selectedMedicalPlans}
              cartOnHide={cartOnHide}
            />
          </>
        ) : (
          <BodyText px={4} pt={4}>
            There are currently no items in your cart.
          </BodyText>
        )}
      </ModalBody>
    </FullScreenModal>
  );
};

const mapDispatchToProps = (dispatch) => ({
  onToggleSavedPlan: (planID, location, fromSidebar) => {
    dispatch(toggleSavePlan(planID, location, fromSidebar));
  },
});

const mapStateToProps = (state) => ({
  selectedMedicalPlans: getSelectedMedicalPlans(state),
  selectedDentalPlan: getSelectedDentalPlan(state),
  selectedVisionPlan: getSelectedVisionPlan(state),
});

const QuotePageCartModal = withRouter(connect(mapStateToProps, mapDispatchToProps)(QuotePageCartModalDumb));

export default QuotePageCartModal;
